import type { ApplicationInsights } from '@microsoft/applicationinsights-web';

/**
 * A frozen tab sends no telemetry: while the main thread is blocked nothing runs, and when the
 * user gives up and reloads, whatever was queued is dropped. So a hang in the design view showed
 * up only as a gap in the logs. Two signals fill it:
 *
 * - Long frames that eventually finish are reported as they end (`LongAnimationFrame` events),
 *   with the scripts the browser attributes them to.
 * - Hangs that end in a reload are reported on the next load. The page records a heartbeat in
 *   sessionStorage (which survives a reload in the same tab) and marks it inactive when it is
 *   hidden or unloaded normally. A reload that kills a blocked page never runs those handlers,
 *   so an active heartbeat that is long stale on a reload means the previous page stopped responding.
 *   Only reloads count: a tab brought back by session restore (or a browser crash) also finds an
 *   active, stale heartbeat in its restored sessionStorage, but nothing hung.
 */

const LONG_FRAME_THRESHOLD_MS = 2_000;
const BEAT_INTERVAL_MS = 1_000;
const HANG_THRESHOLD_MS = 10_000;
/**
 * Nobody waits this long on a frozen tab before reloading it. A heartbeat older than this was left
 * by a page that went away without its unload handlers (browser quit or crash, then session
 * restore), which is not a hang. Backstop for browsers without navigation timing.
 */
const MAX_PLAUSIBLE_HANG_MS = 10 * 60_000;
const BEAT_STORAGE_KEY = 'baseload:main-thread-beat';

interface Beat {
  at: number;
  url: string;
  active: boolean;
}

/** The fields of a `long-animation-frame` script entry used here; not in the TS DOM lib yet. */
interface LongFrameScript {
  invoker?: string;
  sourceURL?: string;
  sourceFunctionName?: string;
  duration: number;
}

function readBeat(): Beat | null {
  try {
    const raw = window.sessionStorage.getItem(BEAT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Beat) : null;
  } catch {
    return null;
  }
}

function writeBeat(active: boolean) {
  try {
    const beat: Beat = { at: Date.now(), url: window.location.href, active };
    window.sessionStorage.setItem(BEAT_STORAGE_KEY, JSON.stringify(beat));
  } catch {
    // Storage blocked or full: hang detection is best-effort.
  }
}

/** The navigation type of this page load, or undefined where navigation timing is unavailable. */
function navigationType(): string | undefined {
  try {
    const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return entry?.type;
  } catch {
    return undefined;
  }
}

/** Reports a hang that the previous page in this tab never got to send. */
function reportPreviousHang(appInsights: ApplicationInsights) {
  const previous = readBeat();
  if (!previous?.active) return;

  const type = navigationType();
  if (type !== undefined && type !== 'reload') return;

  const stalledMs = Date.now() - previous.at;
  if (stalledMs < HANG_THRESHOLD_MS || stalledMs > MAX_PLAUSIBLE_HANG_MS) return;

  appInsights.trackException({
    exception: new Error(`Main thread unresponsive for ${Math.round(stalledMs / 1000)}s before the page was reloaded`),
    severityLevel: 2,
    properties: { kind: 'MainThreadHang', previousUrl: previous.url, stalledMs: String(stalledMs) }
  });
}

function startHeartbeat() {
  const beatWhileVisible = () => {
    if (document.visibilityState === 'visible') writeBeat(true);
  };
  // Hidden tabs are throttled or frozen by the browser, and a discarded one reloads when it is
  // shown again; neither is a hang, so a hidden page is marked inactive.
  const onVisibilityChange = () => writeBeat(document.visibilityState === 'visible');
  const onPageHide = () => writeBeat(false);

  beatWhileVisible();
  const intervalId = window.setInterval(beatWhileVisible, BEAT_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  return () => {
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
  };
}

function describeScripts(scripts: LongFrameScript[]) {
  return [...scripts]
    .sort((a, b) => b.duration - a.duration)
    .slice(0, 3)
    .map(s => `${s.sourceFunctionName || s.invoker || 'anonymous'} @ ${s.sourceURL || 'unknown'} (${Math.round(s.duration)}ms)`)
    .join(' | ');
}

function observeLongFrames(appInsights: ApplicationInsights) {
  const supported = typeof PerformanceObserver !== 'undefined' ? PerformanceObserver.supportedEntryTypes : [];
  // Long animation frames carry script attribution (Chromium); plain long tasks do not.
  const entryType = supported.includes('long-animation-frame') ? 'long-animation-frame' : 'longtask';
  if (!supported.includes(entryType)) return () => {};

  const observer = new PerformanceObserver(list => {
    for (const entry of list.getEntries()) {
      if (entry.duration < LONG_FRAME_THRESHOLD_MS) continue;

      const scripts = (entry as PerformanceEntry & { scripts?: LongFrameScript[] }).scripts ?? [];
      appInsights.trackEvent(
        { name: 'LongAnimationFrame' },
        { entryType, url: window.location.href, scripts: describeScripts(scripts), durationMs: Math.round(entry.duration) }
      );
    }
  });
  observer.observe({ type: entryType, buffered: true });

  return () => observer.disconnect();
}

/** Starts hang reporting; returns a function that stops it. */
export function installHangReporting(appInsights: ApplicationInsights) {
  reportPreviousHang(appInsights);
  const stopHeartbeat = startHeartbeat();
  const stopObserving = observeLongFrames(appInsights);

  return () => {
    stopHeartbeat();
    stopObserving();
  };
}
